/*
 * Request shapes (WORK > Requests). A request is a lead; see requests.ts for its state.
 */
import { z } from "zod";
import { QUOTE_STATUSES } from "./billing.js";
import { fileRow, noteRow } from "./clients.zod.js";
import { REQUEST_STATES } from "./requests.js";
import { HEARD_FROM } from "./heard-from.js";

export const requestRow = z.object({
  id: z.uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  /** What they asked for. */
  service: z.string().nullable(),
  valueCents: z.number().nullable(),
  /** Where they found us (website, google_ads, Typed in and the rest). */
  source: z.string().nullable(),
  /** How they heard about us, in their words picked from HEARD_FROM (the form or the office). */
  heardFrom: z.enum(HEARD_FROM).nullable(),
  clientId: z.uuid().nullable(),
  state: z.enum(REQUEST_STATES),
  archived: z.boolean(),
  quote: z
    .object({
      id: z.uuid(),
      number: z.number(),
      status: z.enum(QUOTE_STATUSES),
      totalCents: z.number(),
      /** What each repeat visit costs (its repeating lines), when it is less than the first visit; else null. */
      repeatCents: z.number().nullable(),
      /** How often it repeats (weekly, biweekly, monthly, one-time). */
      frequency: z.string(),
    })
    .nullable(),
  createdAt: z.string(),
});
export type RequestRow = z.infer<typeof requestRow>;

export const requestList = z.object({ requests: z.array(requestRow) });
export type RequestList = z.infer<typeof requestList>;

/** What was said on an answered call with them (the phone company's summary and words). */
export const callTalkRow = z.object({
  id: z.uuid(),
  direction: z.enum(["in", "out"]),
  at: z.string(),
  seconds: z.number(),
  /** waiting (the phone company is still writing it), done, or none (it has none for this call). */
  status: z.enum(["waiting", "done", "none"]),
  summary: z.array(z.string()),
  nextSteps: z.array(z.string()),
  words: z.array(z.object({ who: z.enum(["them", "us"]), text: z.string(), at: z.number() })),
});
export type CallTalkRow = z.infer<typeof callTalkRow>;

export const requestDetail = requestRow.extend({
  /** What the form sent, or what the office typed. */
  note: z.string().nullable(),
  stage: z.string(),
  utm: z.record(z.string(), z.string()),
  notes: z.array(noteRow),
  /** Yes to texts and the words the form showed (A2P 10DLC). */
  consent: z.array(z.object({ channel: z.string(), kind: z.string(), words: z.string(), at: z.string() })),
  /** The text and email sent back when the request came in. */
  replies: z.array(z.object({ channel: z.string(), status: z.string(), at: z.string() })),
  /** Photos the client added in My Trades, Get a quote. */
  photos: z.array(fileRow),
  /** Answered calls with this phone, newest first. */
  calls: z.array(callTalkRow),
});
export type RequestDetail = z.infer<typeof requestDetail>;
